import {
	IconButtonCompat as IconButton,
	IStackItemStyles,
	LabelCompat as Label,
	SeparatorCompat as Separator,
	StackCompat as Stack,
	TooltipHostCompat as TooltipHost,
} from 'components/FluentUICompat';
import {ControlledTextField, EnumDropdown} from 'components/hookForms';
import React from 'react';
import {
	Control,
	useFieldArray,
	UseFieldArrayAppend,
	UseFieldArrayRemove,
} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {LabelWithTooltip} from 'features/localizedTooltips';
import {AppendButton} from 'components/hookForms/AppendButton';
import {Keyword, TranslationLang, VexCluster} from 'types';

const fieldName = 'descriptions';

export const DescriptionFieldKeyword: React.FC<{control: Control<Keyword>}> = ({
	control,
}) => {
	const {fields, remove, append} = useFieldArray<Keyword>({
		control,
		name: fieldName,
	});

	return (
		<DescriptionFieldBasic
			fields={fields}
			remove={remove}
			append={append}
			control={control}
		/>
	);
};

export const DescriptionFieldVexCluster: React.FC<{
	control: Control<VexCluster>;
}> = ({control}) => {
	const {fields, remove, append} = useFieldArray<VexCluster>({
		control,
		name: fieldName,
	});

	return (
		<DescriptionFieldBasic
			fields={fields}
			remove={remove}
			append={append}
			control={control}
		/>
	);
};

const DescriptionFieldBasic: React.FC<{
	fields: Record<'id', string>[];
	remove: UseFieldArrayRemove;
	append: UseFieldArrayAppend<any, string>;
	control: any;
}> = ({fields, remove, append, control}) => {
	const {t} = useTranslation('components/descriptionfield');

	return (
		<React.Fragment>
			<Label>{t('Description')}</Label>
			<Separator />
			<Stack tokens={{childrenGap: 12}}>
				{fields.map((field, index) => (
					<Stack key={index} tokens={{childrenGap: 4}}>
						<Stack horizontal tokens={{childrenGap: 8}} verticalAlign='center'>
							<Stack.Item styles={dropdownItemStyles}>
								{index === 0 && (
									<LabelWithTooltip translationKey={'lang'}>
										{t('Lang')}
									</LabelWithTooltip>
								)}
								<EnumDropdown
									control={control}
									name={`${fieldName}[${index}].${'lang'}`}
									enumName={'TranslationLang'}
									enumType={TranslationLang}
									defaultValue={TranslationLang.De}
									tooltipHostProps={{shouldGetTextFromContext: false}}
								/>
							</Stack.Item>
							<Stack.Item>
								<TooltipHost content={t('ToolTipDelete')}>
									<IconButton
										styles={{
											root: {marginTop: index === 0 ? 28 : 0},
										}}
										iconProps={{iconName: 'Delete'}}
										onClick={() => remove(index)}
									/>
								</TooltipHost>
							</Stack.Item>
						</Stack>
						{index === 0 && (
							<LabelWithTooltip translationKey={'descriptions'}>
								{t('Description')}
							</LabelWithTooltip>
						)}
						<ControlledTextField
							control={control}
							name={`${fieldName}[${index}].${'translation'}`}
							tooltipHostProps={{shouldGetTextFromContext: false}}
							multiline
							autoAdjustHeight
							resizable={false}
						/>
					</Stack>
				))}
			</Stack>
			<AppendButton
				onClick={() => {
					append({});
				}}
				label={t('AddDescription')}
				name={fieldName}
			/>
		</React.Fragment>
	);
};

const dropdownItemStyles: IStackItemStyles = {
	root: {
		width: 120,
		flexShrink: 0,
	},
};
